import React, { useMemo, useState } from 'react';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../aai/useAsync';
import { usePermissions } from '../../hooks/usePermissions';
import type { AaiContext, ConnectorCatalog, OrbitToolBoard, OrbitToolRow, OrganizationConnections, OwnerControls, OwnerOverview, WhatsAllowedData } from '../../types/aai';
import {
  Action, Chip, Divided, EmptyState, ErrorNote, ListRow, Loading, PageBody, PageHead, Panel, PanelHead, RouteMark,
} from '../../components/aa';
import type { ActivityLinkResolver } from '../partner/ActivityView';
import { ClientContextBody, useLatestCollection } from '../partner/ClientContextPage';
import { ConnectionDrawer, companyConnectionStatus, joinConnections, statusChip } from './ConnectionDrawer';
import { isVersionControl } from '../../components/settings/versionControl';
import { ControlsBody } from './ControlsPage';
// import { DepartmentRoster } from '../department/DepartmentRoster';

/*
 * Connections & access, for the Owner. Still served at /company-context so
 * every link ever sent keeps landing here.
 *
 * The Owner has no separate Company screen, because the Owner IS the company:
 * everything about it is read here. For now only Controls shows, and the page
 * is named for what that is: connections and access. People is commented out
 * rather than removed (the roster lives on /team). The body is shared
 * with the partner console; only the viewer differs (why they can see it, what
 * they may prepare, who decides), plus two things that are the Owner's alone —
 * the company's connections, and the platform controls it runs under.
 *
 * Controls is a tab rather than a destination for the same reason: what the
 * company runs under is part of its context. The old /controls address still
 * renders the page on its own.
 */

// Where an event's record lives for the Owner. No guessed destinations.
export const ownerLinks: ActivityLinkResolver = (event) => {
  if (event.entity_type === 'request' && event.entity_id) return { label: 'Open project', to: `/projects?open=${event.entity_id}` };
  if (event.entity_type === 'opportunity' && event.entity_id) return { label: 'Open opportunity', to: `/projects?tab=opportunities&open=${event.entity_id}` };
  if (event.entity_type === 'skill') return { label: 'Open Quick Wins', to: '/projects?tab=quick_wins' };
  if (event.entity_type === 'member') return { label: 'Open people', to: '/team' };
  if (event.entity_type === 'source') return { label: 'Open company tools', to: '/company-context?tab=sources' };
  // Connections & access hidden for now; control events open the context page instead.
  if (event.entity_type === 'control') return { label: 'Open company context', to: '/company-context' };
  if (event.entity_type === 'portfolio_recommendation' && event.entity_id) return { label: 'Open recommendation', to: `/portfolio?recommendation=${event.entity_id}` };
  return null;
};

const openSettings = (section: string) => window.dispatchEvent(new CustomEvent('settings:open', { detail: { section } }));

const REVIEW_WINDOW_DAYS = 90;

const nextReview = (controls: OwnerControls | null): string => {
  const verified = (controls?.posture || [])
    .map((p) => p.last_verified_at)
    .filter((v): v is string => Boolean(v))
    .map((v) => new Date(v).getTime() + REVIEW_WINDOW_DAYS * 86400000);
  if (verified.length === 0) return 'Not scheduled';
  return new Date(Math.min(...verified)).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const CompanyConnectionsPanel: React.FC<{ canManage: boolean }> = ({ canManage }) => {
  const catalog = useAsync<ConnectorCatalog>(() => aaiApi.getConnectors(), 'Could not load connections.');
  const overview = useAsync<OwnerOverview>(() => aaiApi.getOwnerOverview(), 'Could not load company connections.');
  const coverage = useAsync<OrganizationConnections>(() => aaiApi.getOrganizationConnections(), 'Could not load connection coverage.');
  const [open, setOpen] = useState<string | null>(null);

  const connections = useMemo(() => joinConnections(
    catalog.data?.connectors || [],
    overview.data?.connections || [],
    coverage.data?.personal_coverage || [],
  ), [catalog.data, overview.data, coverage.data]);

  const reload = () => {
    catalog.reload();
    overview.reload();
    coverage.reload();
  };

  if (catalog.loading || overview.loading) return <Panel><Loading label="Loading company connections" /></Panel>;
  if (catalog.error) return <ErrorNote message={catalog.error} />;

  const attention = connections.filter((c) => c.row?.attention).length;
  const selected = connections.find((c) => c.provider === open) || null;

  return (
    <>
      <Panel>
        <PanelHead
          kicker="Company connections"
          title={`${connections.length} connected for the company`}
          desc="Shared with the company by the person who connected them. Records stay in the app; this page never shows them."
          action={(
            <div className="flex items-center gap-3">
              {attention > 0 && <Chip tone="warn">{attention} need{attention === 1 ? 's' : ''} attention</Chip>}
              {canManage && <Action size="sm" variant="quiet" onClick={() => openSettings('connectors')}>Connect a company app</Action>}
            </div>
          )}
        />
        {connections.length === 0 && (
          <EmptyState title="No company connections yet" reason="A connection appears here once someone connects an app for the company." />
        )}
        <Divided>
          {connections.map((c) => {
            const chip = statusChip(companyConnectionStatus(c));
            return (
              <ListRow
                key={c.provider}
                mark={<RouteMark kind="connection" size="lg" />}
                title={c.label}
                badge={(
                  <>
                    <Chip tone={chip.tone} dot>{chip.label}</Chip>
                    {c.row?.attention && <Chip tone="warn">Action required</Chip>}
                  </>
                )}
                desc={c.category}
                meta={[
                  { label: 'Shared by', value: c.versionControl ? 'Your Workspace' : c.row?.owner_email || 'Not recorded' },
                  { label: 'Scope', value: 'Company' },
                  { label: 'Personal', value: `${c.membersConnected} connected` },
                ]}
                onClick={() => setOpen(c.provider)}
                arrow
              />
            );
          })}
        </Divided>
      </Panel>

      {selected && (
        <ConnectionDrawer connection={selected} canManage={canManage} onClose={() => setOpen(null)} onChanged={reload} />
      )}
    </>
  );
};

/* The tools the company says it runs on, or added, that nobody has connected
   for the company yet. Without this, a tool named in Orbit's questions
   appeared nowhere on the page the answer pointed to (BLK-135). It sits under
   the controls because that is the part of this page the Owner sees. Code
   hosts are left to the Connectors list above it, which already shows them. */
const NotConnectedTools: React.FC<{ canManage: boolean }> = ({ canManage }) => {
  const board = useAsync<OrbitToolBoard>(() => aaiApi.getOrbitTools(), 'Could not load the company tools.');
  const notConnected = (board.data?.tools || []).filter((t): t is OrbitToolRow & { connector: NonNullable<OrbitToolRow['connector']> } => (
    Boolean(t.connector) && t.on_stack && !t.connector!.organization_status && !isVersionControl(t.connector!)
  ));
  if (notConnected.length === 0) return null;
  let toolWord = 'tools';
  if (notConnected.length === 1) toolWord = 'tool';
  return (
    <div className="mt-5">
      <Panel>
        <PanelHead
          kicker="Not connected yet"
          title={`${notConnected.length} ${toolWord} your company uses`}
          desc="Named in Orbit's questions or added to your tools, and not connected for the company. Connect one so Orbit can work in it."
        />
        <Divided>
          {notConnected.map((t) => {
            let action: React.ReactNode = <Chip>Not connected</Chip>;
            if (canManage && t.connector.connect_here && t.connector.enabled) {
              action = <Action size="sm" variant="quiet" arrow onClick={() => openSettings('connectors')}>Connect</Action>;
            }
            let desc = t.named_by || 'On your company tools';
            if (!t.connector.enabled) desc = 'Switched off for the company. Switch it on in Connectors to connect it.';
            if (!t.connector.connect_here && t.connector.setup_hint) desc = t.connector.setup_hint;
            return (
              <ListRow
                key={t.key}
                mark={<RouteMark kind="connection" size="lg" />}
                title={t.label}
                desc={desc}
                meta={[{ label: 'Category', value: t.category_label }]}
                right={action}
              />
            );
          })}
        </Divided>
      </Panel>
    </div>
  );
};

const OwnerCompanyContextPage: React.FC = () => {
  const { can } = usePermissions();
  const clientId = localStorage.getItem('client_id') || '';
  const clientName = localStorage.getItem('client_name') || 'Your company';

  const context = useAsync<AaiContext>(() => aaiApi.getContext(), 'Could not load your context.');
  const allowed = useAsync<WhatsAllowedData>(() => aaiApi.getWhatsAllowed(), 'Could not load company context.');
  const cycle = useLatestCollection(clientId || undefined);

  if (allowed.loading) return <PageBody><Loading label="Loading company context" /></PageBody>;
  if (allowed.error || !allowed.data) return <PageBody><ErrorNote message={allowed.error || 'Something went wrong.'} /></PageBody>;

  const companyName = context.data?.company_name || clientName;

  return (
    <PageBody>
      <PageHead
        align="center"
        title="Connections & access"
        sub="The apps connected for the company, the tools departments have asked for, and the access requests waiting on you."
      />

      <ClientContextBody
        viewer="owner"
        clientId={clientId}
        clientName={companyName}
        allowed={allowed.data}
        cycle={cycle}
        links={ownerLinks}
        beforeSources={<CompanyConnectionsPanel canManage={can('aai_manage_connectors')} />}
        // people={<DepartmentRoster />}
        // Connections & access tab hidden for now.
        // controls={(
        //   <>
        //     <ControlsBody />
        //     <NotConnectedTools canManage={can('aai_manage_connectors')} />
        //   </>
        // )}
        room={false}
      />
    </PageBody>
  );
};

export default OwnerCompanyContextPage;
